/**
 * Attribution helper for The Neural Feed.
 * Captures first-touch and session attribution:
 * utm_source, utm_medium, utm_campaign, utm_content, utm_term,
 * referrer (external only), landing_path (cleaned), first_seen.
 *
 * sessionStorage: 'tnf_attr' (per visit/session; overwritten only on new UTM landing)
 * localStorage: 'tnf_attr_first' (first touch, 30-day expiry; never overwritten while valid)
 */

export function cleanLandingPath(loc) {
  try {
    const l = loc || (typeof window !== 'undefined' ? window.location : null);
    if (!l) return '';
    const sp = new URLSearchParams(l.search || '');
    const clean = new URLSearchParams();
    sp.forEach((val, key) => {
      if (!key.toLowerCase().includes('email') && !val.includes('@')) {
        clean.append(key, val);
      }
    });
    const qs = clean.toString();
    return (l.pathname || '/') + (qs ? '?' + qs : '');
  } catch (e) {
    return typeof window !== 'undefined' ? window.location.pathname : '';
  }
}

export function initAttribution() {
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search);
    const uSource = params.get('utm_source');
    const uMed = params.get('utm_medium');
    const uCamp = params.get('utm_campaign');
    const uCont = params.get('utm_content');
    const uTerm = params.get('utm_term');
    const hasUtm = Boolean(uSource || uMed || uCamp || uCont || uTerm);

    let existingSess = null;
    try {
      const sRaw = sessionStorage.getItem('tnf_attr');
      if (sRaw) existingSess = JSON.parse(sRaw);
    } catch (e) {}

    // Only update session attribution if there are UTMs in URL or no session attribution yet
    if (hasUtm || !existingSess) {
      let ref = document.referrer || null;
      if (ref) {
        try {
          const refHost = new URL(ref).hostname.toLowerCase();
          if (refHost.includes('theneuralfeed.com') || refHost === 'localhost') {
            ref = null;
          }
        } catch (e) {
          ref = null;
        }
      }

      const attr = {
        utm_source: uSource || (existingSess && existingSess.utm_source) || null,
        utm_medium: uMed || (existingSess && existingSess.utm_medium) || null,
        utm_campaign: uCamp || (existingSess && existingSess.utm_campaign) || null,
        utm_content: uCont || (existingSess && existingSess.utm_content) || null,
        utm_term: uTerm || (existingSess && existingSess.utm_term) || null,
        referrer: ref || (existingSess && existingSess.referrer) || null,
        landing_path: cleanLandingPath(),
        first_seen: new Date().toISOString()
      };

      try {
        sessionStorage.setItem('tnf_attr', JSON.stringify(attr));
      } catch (e) {}

      // Keep first touch in localStorage (30-day expiry)
      try {
        const firstRaw = localStorage.getItem('tnf_attr_first');
        let keepFirst = false;
        if (firstRaw) {
          const firstObj = JSON.parse(firstRaw);
          if (firstObj && firstObj.first_seen) {
            const age = Date.now() - new Date(firstObj.first_seen).getTime();
            if (age < 30 * 24 * 60 * 60 * 1000) {
              keepFirst = true;
            }
          }
        }
        if (!keepFirst) {
          localStorage.setItem('tnf_attr_first', JSON.stringify(attr));
        }
      } catch (e) {}
    }
  } catch (e) {}
}

export function getAttributionPayload(currentPage) {
  let attr = null;
  try {
    const s = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('tnf_attr') : null;
    if (s) attr = JSON.parse(s);
  } catch (e) {}

  if (!attr) {
    try {
      const f = typeof localStorage !== 'undefined' ? localStorage.getItem('tnf_attr_first') : null;
      if (f) {
        const parsed = JSON.parse(f);
        if (parsed && parsed.first_seen) {
          const age = Date.now() - new Date(parsed.first_seen).getTime();
          if (age < 30 * 24 * 60 * 60 * 1000) {
            attr = parsed;
          }
        }
      }
    } catch (e) {}
  }

  attr = attr || {};
  const page = currentPage || (typeof window !== 'undefined' ? window.location.pathname : '');

  const payload = {};
  if (attr.utm_source) payload.utm_source = attr.utm_source;
  if (attr.utm_medium) payload.utm_medium = attr.utm_medium;
  if (attr.utm_campaign) payload.utm_campaign = attr.utm_campaign;
  if (attr.utm_content) payload.utm_content = attr.utm_content;
  if (attr.utm_term) payload.utm_term = attr.utm_term;
  if (attr.referrer) payload.referrer = attr.referrer;
  if (attr.landing_path) payload.landing_path = attr.landing_path;
  if (page) payload.signup_page = page;

  return payload;
}
